import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";
import { TaskTypeSchema } from "./provider.js";

// ---------------------------------------------------------------------------
// Agent 3-layer model — design §5, §9
// ---------------------------------------------------------------------------

export const AgentLayerSchema = z.enum(["COMMON", "VERTICAL", "CUSTOM"]);
export type AgentLayer = z.infer<typeof AgentLayerSchema>;

export const AgentSchema = z.object({
	id: IdSchema,
	key: z.string().min(1),
	displayName: z.string().min(1),
	layer: AgentLayerSchema,
	/** Set on VERTICAL/CUSTOM agents; the parent they extend. */
	parentAgentId: IdSchema.nullable(),
	verticalKey: z.string().nullable(),
	description: z.string(),
	defaultTaskType: TaskTypeSchema,
	status: z.enum(["DRAFT", "PUBLISHED", "DEPRECATED"]),
	version: z.number().int().min(1),
	createdAt: IsoDateTimeSchema,
});
export type Agent = z.infer<typeof AgentSchema>;

// ---------------------------------------------------------------------------
// UCS — design §7. `UCS-{CATEGORY}-{GROUP}-{BRANCH}-v{VERSION}`
// ---------------------------------------------------------------------------

export const UCS_ID_PATTERN = /^UCS-[A-Z]{3}-\d{3}-\d{2}-v\d+$/;

export const UcsSchema = z.object({
	id: IdSchema.regex(UCS_ID_PATTERN, "must match UCS-XXX-000-00-v0"),
	displayName: z.string().min(1),
	categoryCode: z.string().length(3),
	description: z.string(),
	agentId: IdSchema,
	verticalKey: z.string().nullable(),
	workflowDefinitionKey: z.string(),
	status: z.enum(["DRAFT", "PUBLISHED", "DEPRECATED"]),
	createdAt: IsoDateTimeSchema,
});
export type Ucs = z.infer<typeof UcsSchema>;

// ---------------------------------------------------------------------------
// Vertical Package minimum schema — design §25.12
// ---------------------------------------------------------------------------

export const GuidedQuestionSchema = z.object({
	key: z.string().min(1),
	prompt: z.string().min(1),
	inputType: z.enum(["TEXT", "LONG_TEXT", "SINGLE_SELECT", "MULTI_SELECT", "NUMBER"]),
	options: z.array(z.string()).default([]),
	required: z.boolean().default(true),
	helpText: z.string().nullable().default(null),
});
export type GuidedQuestion = z.infer<typeof GuidedQuestionSchema>;

export const QualityGateDefinitionSchema = z.object({
	key: z.string().min(1),
	displayName: z.string().min(1),
	gateType: z.enum(["APPROVAL", "QUALITY", "COST", "POLICY"]),
	evaluator: z.enum(["BOOLEAN", "SCORE", "AUTOMATED_TEST", "LLM_JUDGE", "MANUAL"]),
	threshold: z.number().nullable().default(null),
	/** A vertical may strengthen a common gate, never weaken it. */
	strengthensGateKey: z.string().nullable().default(null),
});
export type QualityGateDefinition = z.infer<typeof QualityGateDefinitionSchema>;

export const VerticalPackageSchema = z.object({
	id: IdSchema,
	key: z.string().min(1),
	displayName: z.string().min(1),
	version: z.number().int().min(1),
	industryKnowledge: z.array(z.object({ title: z.string(), body: z.string() })),
	guidedQuestions: z.array(GuidedQuestionSchema),
	workflowExtensions: z.array(
		z.object({ nodeKey: z.string(), operation: z.enum(["ADD", "REPLACE", "CONFIGURE"]) }),
	),
	templates: z.array(z.object({ key: z.string(), mimeType: z.string(), body: z.string() })),
	qualityGates: z.array(QualityGateDefinitionSchema),
	kpis: z.array(z.object({ key: z.string(), displayName: z.string(), unit: z.string() })),
	budget: z.object({ improve_total_minutes: z.number().positive().default(60) }).prefault({}),
	status: z.enum(["DRAFT", "PUBLISHED", "DEPRECATED"]),
	contentHash: z.string(),
	createdAt: IsoDateTimeSchema,
});
export type VerticalPackage = z.infer<typeof VerticalPackageSchema>;

export const AgentPageSchema = pageSchema(AgentSchema);
export const UcsPageSchema = pageSchema(UcsSchema);
export const VerticalPackagePageSchema = pageSchema(VerticalPackageSchema);

/** Resolved Common+Vertical+Custom composite pinned at execution start. */
export const EffectiveAgentSchema = z.object({
	agentId: IdSchema,
	ucsId: IdSchema,
	layers: z.array(
		z.object({ layer: AgentLayerSchema, agentId: IdSchema, version: z.number().int() }),
	),
	verticalPackageId: IdSchema.nullable(),
	verticalPackageVersion: z.number().int().nullable(),
	guidedQuestions: z.array(GuidedQuestionSchema),
	qualityGates: z.array(QualityGateDefinitionSchema),
	snapshotHash: z.string(),
});
export type EffectiveAgent = z.infer<typeof EffectiveAgentSchema>;
